/**
 * 予約管理ドメインの共有型定義。
 * feast-be の予約 API 契約（docs/requirements/0005-reservation.md +
 * Phase 1 追加契約 reservation-phase1-api-contract.md）に対応する。
 * 日時は JST 前提の ISO-8601（タイムゾーンなし LocalDateTime 例: 2026-07-10T18:30:00）。
 */

export type ReservationStatus =
  | 'REQUESTED'
  | 'CONFIRMED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'NO_SHOW'
  // Phase 2（3DSデポジット・統合契約 A0）。PENDING_PAYMENT=決済待ち（非終端）/ EXPIRED=ホールド失効（終端）。
  // 通常の台帳/一覧では出さない扱いだが、集計・照会で来うるため型・ラベル・色を用意する。
  | 'PENDING_PAYMENT'
  | 'EXPIRED';

export type ReservationStatusAction = 'CONFIRM' | 'REJECT' | 'CANCEL' | 'COMPLETE' | 'NO_SHOW';

export type InquiryType = 'TEXT' | 'NUMBER' | 'CHOICE' | 'BOOLEAN';
export type ApprovalMode = 'AUTO' | 'MANUAL';
export type CapacityMode = 'AGGREGATE' | 'SEAT_TYPE';
export type PaymentType = 'DEPOSIT_FIXED' | 'DEPOSIT_PER_PERSON' | 'COURSE_FULL';
export type PaymentStatus =
  | 'AUTHORIZED'
  | 'CAPTURED'
  | 'CANCELED'
  | 'PARTIALLY_REFUNDED'
  | 'REFUNDED'
  | 'FAILED';
export type CancellationReason = 'CUSTOMER' | 'STORE';
export type SeatAssignedBy = 'AUTO' | 'STAFF';

// ---------- スタッフ予約 ----------

/** GET /stores/{id}/reservations の一覧アイテム */
export interface ReservationListItem {
  id: string;
  reservationNumber: string;
  userName: string;
  reservationTime: string;
  numberOfPeople: number;
  status: ReservationStatus;
  selectedMenuName: string | null;
  /** 割当席タイプ名（未割当は null。旧BEは未返却のため optional。フィードバックE4） */
  seatTypeName?: string | null;
  /** 消化卓数（未割当は null。旧BEは未返却のため optional。フィードバックE4） */
  units?: number | null;
  /**
   * 個人情報が匿名化済みか（ご予約の来店日から15日経過後に日次バッチで非可逆匿名化・Wave B）。
   * true のとき氏名/メール/電話は復元不可のダミー値。旧BEは未返却のため optional（未指定=未匿名化）。
   */
  anonymized?: boolean;
}

export interface ReservationPaymentView {
  amount: number;
  currency: string;
  status: PaymentStatus;
  refundedAmount: number;
}

export interface InquiryResponseView {
  question: string;
  inquiryType: InquiryType;
  answer: string;
}

export interface SeatAssignmentView {
  seatTypeId: string;
  seatTypeName: string;
  assignedBy: SeatAssignedBy;
  /** この予約が消化する卓数（席の連結時は2以上。旧BEは未返却のため optional・省略時1扱い） */
  units?: number;
}

/**
 * 予約詳細のその時間帯における席タイプ別在庫サマリ（統合契約5・additive）。
 * occupiedUnits は対象予約の [start,end) と重なる占有卓数の合計。
 */
export interface SeatInventorySnapshotItem {
  seatTypeId: string;
  seatTypeName: string;
  unitCount: number;
  occupiedUnits: number;
}

/** GET /stores/{id}/reservations/{rid} の詳細ビュー（Phase 1 追加項目含む） */
export interface ReservationDetailView {
  id: string;
  reservationNumber: string;
  userName: string;
  userEmail: string | null;
  userPhone: string | null;
  reservationTime: string;
  numberOfPeople: number;
  status: ReservationStatus;
  cancellationReason: CancellationReason | null;
  selectedMenuName: string | null;
  selectedMenuPrice: number | null;
  responses: InquiryResponseView[];
  payment: ReservationPaymentView | null;
  // ---- Phase 1 追加（BE 実装中の契約） ----
  stayMinutes?: number;
  endTime?: string;
  seatAssignment?: SeatAssignmentView | null;
  staffNote?: string | null;
  /** 対象予約の時間帯における席タイプ別在庫サマリ（統合契約5・additive。旧BEは未返却） */
  seatInventorySnapshot?: SeatInventorySnapshotItem[];
  /**
   * 個人情報が匿名化済みか（ご予約の来店日から15日経過後に日次バッチで非可逆匿名化・Wave B）。
   * true のとき userName/userEmail/userPhone・回答は復元不可のダミー値のため、
   * 実データの代わりに安心文言を表示する。旧BEは未返却のため optional（未指定=未匿名化）。
   */
  anonymized?: boolean;
}

/** POST /stores/{id}/reservations・POST /public/stores/{id}/reservations の結果 */
export interface CreateReservationResult {
  reservationId: string;
  reservationNumber: string;
  managementToken: string;
  status: ReservationStatus;
  warnings: string[];
}

/** 予約作成/変更の入力（スタッフ・公開共通のボディ形） */
export interface ReservationInput {
  userName: string;
  email: string | null;
  phone: string | null;
  reservationTime: string;
  numberOfPeople: number;
  selectedMenuId: string | null;
  responses: { inquiryId: string; content: string }[];
}

/** GET /stores/{id}/reservations/{rid}/guest-history（Phase 1 追加） */
export interface GuestHistoryView {
  visits: number;
  cancellations: number;
  noShows: number;
  reservations: {
    id: string;
    dateTime: string;
    partySize: number;
    status: ReservationStatus;
    /** 個人情報が匿名化済みか（Wave B。過去予約履歴でも匿名化分を安心文言で示す）。旧BEは未返却のため optional */
    anonymized?: boolean;
  }[];
}

// ---------- ダッシュボード集計（統合契約 B・additive） ----------

/** ダッシュボードの集計粒度。HOUR=選択日の時間帯別 / DAY=その月の各日 / MONTH=anchor月-1〜anchor月+12 */
export type DashboardGranularity = 'HOUR' | 'DAY' | 'MONTH';

/** 1ステータス分の集計値（count=予約件数, people=来店人数合計） */
export interface DashboardStatusValue {
  count: number;
  people: number;
}

/**
 * ダッシュボードの1バケット（時間帯 / 日 / 月）。
 * byStatus は全ステータス 0 埋めで返る前提だが、旧BE後方互換のため各値は optional 扱いで参照する。
 */
export interface DashboardBucket {
  /** 表示ラベル（例: '18時' / '7/10(木)' / '2026-07'） */
  label: string;
  /** バケット識別キー（HOUR は時、DAY は 'YYYY-MM-DD'、MONTH は 'YYYY-MM' 等） */
  key: string;
  byStatus: Partial<Record<ReservationStatus, DashboardStatusValue>>;
}

/** GET /stores/{id}/reservations/dashboard のレスポンス */
export interface DashboardResponse {
  buckets: DashboardBucket[];
}

// ---------- 台帳（timeline・Phase 1 追加） ----------

export interface TimelineEntry {
  reservationId: string;
  reservationNumber: string;
  guestName: string;
  partySize: number;
  status: ReservationStatus;
  start: string;
  end: string;
  /** null = 未割当レーン */
  seatTypeId: string | null;
  assignedBy: SeatAssignedBy | null;
  /** この予約が消化する卓数（席の連結時は2以上。旧BEは未返却のため optional・省略時1扱い） */
  units?: number;
}

export interface TimelineView {
  date: string;
  capacityMode: CapacityMode;
  hours: { open: string; close: string }[];
  seatTypes: ReservationSeatType[];
  entries: TimelineEntry[];
}

// ---------- 空き状況・営業カレンダー ----------

/**
 * 枠が予約不可（bookable=false）の理由（予約STGフィードバック第3弾 Wave A・additive）。
 * FULL: 在庫（卓数）が埋まって満席。
 * AFTER_HOURS: コース/デフォルト滞在で slot+stay が営業終了を超える（満席表現にしない）。
 * null: 予約可、または別理由。旧BEは未返却のため optional（未指定時は従来どおり「満席」表示）。
 * additive なため未知値も許容する。
 */
export type UnavailableReason = 'FULL' | 'AFTER_HOURS' | string;

export interface AvailabilitySlot {
  dateTime: string;
  remainingParties: number;
  remainingPeople: number;
  bookable: boolean;
  /** bookable=false のときの理由（旧BEは未返却のため optional） */
  unavailableReason?: UnavailableReason | null;
}

export interface BusinessCalendarDay {
  date: string;
  open: boolean;
  hours: { open: string; close: string }[];
  closedReason: 'REGULAR' | 'SPECIAL' | null;
  /**
   * その日に実際に予約可能な時間枠が1つ以上あるか（営業日＋受付期間＋締切/現在時刻/在庫を考慮）。
   * 旧BEは未返却のため optional。undefined のときは open で代替判定する（後方互換）。
   */
  bookable?: boolean;
}

// ---------- 設定・カタログ ----------

/** GET/PUT /stores/{id}/reservation-setting（Phase 1 追加項目含む） */
export interface ReservationSetting {
  approvalMode: ApprovalMode;
  slotIntervalMinutes: number;
  maxPartiesPerSlot: number;
  maxPeoplePerSlot: number;
  minPartySize: number;
  maxPartySize: number;
  acceptanceFromDays: number;
  acceptanceUntilDays: number;
  acceptanceCutoffMinutes: number;
  reminderOffsetHours: number;
  requestApprovalSlaHours: number;
  // ---- Phase 1 追加 ----
  capacityMode: CapacityMode;
  defaultStayMinutes: number;
  selfCancelDeadlineHours: number;
  phoneRequired: boolean;
  mailCustomMessage: string | null;
  /** 予約ページの公開/非公開（フィードバック1・migration 019 追加。旧BEは未返却のため optional） */
  publicPageEnabled?: boolean;
  // ---- フィードバック4追加（migration 022。旧BEは未返却のため optional） ----
  /** true=コース選択必須 / false（既定）=席のみ予約OK（登録コースが0件のときは強制しない） */
  courseSelectionRequired?: boolean;
  /** 消費税率（%）。税込表示・税込プレビューの算出に使う（既定10・1〜99） */
  taxRatePercent?: number;
  // ---- Wave A 追加（migration 024。旧BEは未返却のため optional） ----
  /**
   * オーバーブッキング許可（承認制(MANUAL)店舗で卓数超過の公開予約を受け付けるか）。
   * 既定 false。設定 UI は iOS 側で提供（FE は型整合・保存往復のため保持）。
   */
  allowOverbooking?: boolean;
  /**
   * 席の連結を含む実効的な最大受入人数（統合契約4・A1・GET のみ・読み取り専用）。
   * 有効な席タイプ全体の max(capacityMax×maxCombinedUnits)。席タイプが無い/AGGREGATE時は null。
   * 人数変更の上限警告に maxPartySize より優先して使う（旧BEは未返却のため optional）。
   */
  maxSeatCapacity?: number | null;
}

/** 消費税率の既定値（BE の migration 022 既定と揃える） */
export const DEFAULT_TAX_RATE_PERCENT = 10;

/**
 * 税抜額と税率(%)から税込額を算出する（端数切り捨て・契約B）。
 * 税込額 = floor(税抜 × (1 + rate/100))。rate 未指定時は既定10%。
 */
export const calcPriceIncludingTax = (
  priceExcludingTax: number,
  taxRatePercent: number = DEFAULT_TAX_RATE_PERCENT,
): number => Math.floor(priceExcludingTax * (1 + taxRatePercent / 100));

/** GET /stores/{id}/reservation-mail-preview?type= の種類（フィードバック1追加） */
export type ReservationMailPreviewType = 'RECEIVED' | 'CONFIRMED' | 'CANCELLED' | 'REMINDER';

/** メール文プレビューのレスポンス */
export interface ReservationMailPreview {
  subject: string;
  body: string;
}

export const MAIL_PREVIEW_TYPE_LABEL: Record<ReservationMailPreviewType, string> = {
  RECEIVED: '受付',
  CONFIRMED: '確定',
  CANCELLED: 'キャンセル',
  REMINDER: 'リマインダー',
};

/** 席タイプ（予約コンテキスト独自マスタ・Phase 1 追加） */
export interface ReservationSeatType {
  id: string;
  name: string;
  capacityMin: number;
  capacityMax: number;
  unitCount: number;
  sortIndex: number;
  /** 1組で使える最大卓数（席の連結）。1=結合なし。2以上でその卓数まで連結可（旧BEは未返却のため optional・省略時1扱い） */
  maxCombinedUnits?: number;
}

export interface ReservationSeatTypeInput {
  name: string;
  capacityMin: number;
  capacityMax: number;
  unitCount: number;
  sortIndex: number;
  /** 1組で使える最大卓数（席の連結）。省略時1 */
  maxCombinedUnits?: number;
}

/** GET /stores/{id}/business-hours の行 */
export interface BusinessHourSlot {
  id: string;
  /** 1=月 ... 7=日（ISO） */
  dayOfWeek: number;
  open: string;
  close: string;
}

export interface SpecialDay {
  id: string;
  storeId: string;
  date: string;
  isClosed: boolean;
  hours: { open: string; close: string }[];
}

/**
 * 予約設定変更が既存予約に影響する場合に BE が返す 409 の要約（フィードバック5・B4/B5）。
 * - 臨時休業化（特定日）: その日に有効な予約があると 409。
 * - 営業時間変更: 変更後の枠外になる将来予約があり confirm 未指定だと 409。
 * BE本文（{status,message,affectedCount,...}）から抽出する。affectedCount 等は旧BEでは無いこともある。
 */
export interface ReservationConflict {
  /** HTTPステータス（409 を影響予約ありの判定に使う。取得できない場合は undefined） */
  status?: number;
  /** BE のエラーメッセージ（表示用フォールバック） */
  message: string;
  /** 影響を受ける予約件数（旧BEは未返却のため optional） */
  affectedCount?: number;
}

export interface ReservationInquiryChoice {
  id: string;
  label: string;
  sortIndex: number;
}

export interface ReservationInquiry {
  id: string;
  storeId: string;
  type: InquiryType;
  content: string;
  required: boolean;
  sortIndex: number;
  choices: ReservationInquiryChoice[];
}

export interface ReservationInquiryInput {
  type: InquiryType;
  content: string;
  required: boolean;
  sortIndex: number;
  choices: string[];
}

/** コースメニュー（stayMinutes は Phase 1、imageUrls はフィードバック2追加） */
export interface ReservationMenu {
  id: string;
  storeId?: string;
  name: string;
  price: number;
  description: string | null;
  sortIndex: number;
  stayMinutes?: number | null;
  /** コース画像（最大3枚・並び順=表示順。旧BEは未返却のため optional） */
  imageUrls?: string[];
  /** 税込コース価格（フィードバック4・additive。旧BEは未返却のため optional。無ければ price から算出） */
  priceIncludingTax?: number;
}

export interface ReservationMenuInput {
  name: string;
  price: number;
  description: string | null;
  sortIndex: number;
  stayMinutes: number | null;
  /** コース画像（最大3枚・並び順=表示順） */
  imageUrls: string[];
}

export interface RefundTier {
  hoursBeforeStart: number;
  refundPercent: number;
}

// ---------- 予約デポジット決済（Phase 2・3DS・統合契約 A3/A4/A7・additive） ----------

/**
 * 公開予約フォームに返る決済ポリシー（統合契約 A7・additive）。
 * FE はこれで決済ステップ要否（paymentEnabled && paymentRequired）とキャンセルポリシー表示を判断する。
 * stripePublishableKey は返さない（プラットフォームの pk は FE の環境変数を使う）。
 * 旧BE（Phase 2 未対応）は未返却のため form 側で optional にしている。
 */
export interface PublicPaymentPolicy {
  paymentEnabled: boolean;
  paymentRequired: boolean;
  paymentType: PaymentType;
  /**
   * 人数/コース未確定時は概算 or null。確定入力後の checkout 応答 amount を正とする。
   * 統合契約 A7 / feast-be ADR 0103 §7 で税込額に修正済み（お客様への提示・返金換算の基準）。
   */
  depositAmountPreview?: number | null;
  currency: string;
  /** キャンセルポリシー段階（開始 N 時間前までの返金率）。表示・同意に使う */
  cancellationPolicyTiers: RefundTier[];
  /**
   * 顧客都合キャンセル時の決済手数料の負担者（feast-be ADR 0103）。
   * STORE_ABSORBS=店舗が負担（満額返金）/ CUSTOMER_BEARS=顧客が負担（手数料分を差し引いて返金）。
   * additive なため未知値も許容する（旧BEは未返却）。
   */
  refundFeePolicy: string;
}

/**
 * POST /public/stores/{id}/reservations/checkout のレスポンス（統合契約 A3）。
 * 予約を PENDING_PAYMENT で作成し PaymentIntent(confirm=false) を作った結果。
 * managementToken はまだ返らない（finalize で受け取る）。clientSecret は永続・ログ禁止。
 */
export interface CheckoutResponse {
  reservationId: string;
  clientSecret: string;
  stripeAccountId: string;
  amount: number;
  currency: string;
  /** ホールド期限（ISO-8601）。超過で再試行不可＝再入力を促す */
  expiresAt: string;
}

/**
 * POST /public/stores/{id}/reservations/{reservationId}/finalize のレスポンス（統合契約 A4）。
 * PaymentIntent を Stripe 真実源で照会・検証して確定した結果（冪等）。
 * 未完了（requires_action/processing 等）のときは finalized=false + status を返すか 409。
 */
export interface FinalizeResponse {
  reservationId: string;
  reservationNumber?: string;
  status: ReservationStatus | 'PENDING_PAYMENT';
  managementToken?: string;
  managementLink?: string;
  /** 未完了時に false。省略時（成功レスポンス）は確定とみなす */
  finalized?: boolean;
}

/** GET/PUT /stores/{id}/payment-setting */
export interface PaymentSetting {
  paymentEnabled: boolean;
  paymentRequired: boolean;
  paymentType: PaymentType;
  depositAmount: number | null;
  tiers: RefundTier[];
  /**
   * 顧客都合キャンセル時の決済手数料の負担者（feast-be ADR 0103・既定 STORE_ABSORBS）。
   * STORE_ABSORBS=店舗が負担（満額返金）/ CUSTOMER_BEARS=顧客が負担（手数料分を差し引いて返金）。
   */
  refundFeePolicy: 'STORE_ABSORBS' | 'CUSTOMER_BEARS';
}

/** GET /stores/{id}/stripe（連携ステータス） */
export interface StripeConnectionStatus {
  available: boolean;
  connected: boolean;
  chargesEnabled: boolean;
  livemode: boolean;
}

/** 個人情報の取り扱いの提示方法（フィードバック3・migration 021）。URL=外部リンク / TEXT=FEAST 公開ページに掲載 */
export type PrivacyPolicyType = 'URL' | 'TEXT';

/**
 * GET/PUT /stores/{id}/store-info（予約ドメインの店舗案内情報）。
 * フィードバック2（migration 020）で D 項目を追加。追加項目はすべて任意（null 可）で、
 * 旧 BE は未返却のため optional にしている。
 * フィードバック3（migration 021）で個人情報の取り扱い3項目を追加（未設定の店舗は公開受付不可）。
 */
export interface ReservationStoreInfo {
  contactInfo: string;
  note: string | null;
  // ---- フィードバック2追加（すべて任意） ----
  phone?: string | null;
  accessInfo?: string | null;
  businessHoursNote?: string | null;
  closedDaysNote?: string | null;
  paymentMethodsNote?: string | null;
  serviceChargeNote?: string | null;
  cancellationPolicyNote?: string | null;
  dressCodeNote?: string | null;
  seatsNote?: string | null;
  smokingNote?: string | null;
  childrenNote?: string | null;
  petNote?: string | null;
  parkingNote?: string | null;
  storeNotes?: string | null;
  /** 店舗マスタから引き継ぎ（GET のみ・読み取り専用。変更は /manage/store で行う） */
  storeName?: string | null;
  /** 店舗マスタから引き継ぎ（GET のみ・読み取り専用） */
  address?: string | null;
  // ---- フィードバック3追加（個人情報の取り扱い・migration 021。旧BEは未返却のため optional） ----
  /** null=未設定（未設定の間は公開予約ページで受付できない） */
  privacyPolicyType?: PrivacyPolicyType | null;
  /** type=URL のときの掲載先 URL */
  privacyPolicyUrl?: string | null;
  /** type=TEXT のときの掲載本文（FEAST の公開ページに店舗名で掲載） */
  privacyPolicyText?: string | null;
}

/** 予約画像アップロード presign の対象種別（コース画像 / 店舗写真） */
export type ReservationImageKind = 'COURSE' | 'STORE';

/** GET/PUT /stores/{id}/reservation-store-images（店舗写真: ヒーロー1枚+ギャラリー最大6枚） */
export interface ReservationStoreImages {
  heroImageUrl: string | null;
  galleryImageUrls: string[];
}

// ---------- 公開予約 ----------

/**
 * 公開受付を停止している理由（フィードバック3・additive）。
 * PRIVACY_POLICY_MISSING は店舗都合の設定漏れのため、お客様向けには通常の
 * 「受付していません」表示のままにする（出し分けしない）。
 */
export type NotAcceptingReason =
  | 'PRIVACY_POLICY_MISSING'
  | 'PAGE_DISABLED'
  | 'SUBSCRIPTION'
  | 'CONTACT_MISSING';

/** 公開予約フォームに返る個人情報の取り扱い（TEXT 型は url=null で FE が自ページへリンクする） */
export interface PublicPrivacyPolicyRef {
  type: PrivacyPolicyType;
  url: string | null;
}

/** GET /public/stores/{id}/privacy-policy（type=TEXT の店舗のみ 200・他は 404） */
export interface PublicPrivacyPolicyView {
  storeName: string;
  text: string;
}

export interface PublicInquiryView {
  id: string;
  type: InquiryType;
  content: string;
  required: boolean;
  sortIndex: number;
  choices: string[];
}

export interface PublicMenuView {
  id: string;
  name: string;
  price: number;
  description: string | null;
  sortIndex: number;
  stayMinutes?: number | null;
  /** コース画像（フィードバック2追加。1枚目をサムネイル表示） */
  imageUrls?: string[];
  /** 税込コース価格（フィードバック4・additive。無ければ price から算出。公開フォームは税込表示） */
  priceIncludingTax?: number;
}

/** GET /public/stores/{id}/reservation-form（Phase 1 追加項目含む） */
export interface PublicReservationFormView {
  /** 店舗案内情報（フィードバック2で D 項目・storeName/address が追加される） */
  storeInfo: ReservationStoreInfo | null;
  paymentEnabled: boolean;
  paymentRequired: boolean;
  /**
   * 決済ポリシー（Phase 2・3DS・統合契約 A7・additive）。
   * paymentEnabled && paymentRequired のとき FE は確認の後に決済ステップを挿入する。
   * cancellationPolicyTiers はキャンセル返金段階の表示・同意に使う。旧BEは未返却のため optional。
   */
  payment?: PublicPaymentPolicy;
  inquiries: PublicInquiryView[];
  menus: PublicMenuView[];
  // ---- Phase 1 追加 ----
  acceptingReservations?: boolean;
  /** 最短受付可能日（today + acceptanceFromDays）。受付開始オフセットをカレンダー最短日に反映する。未返却(旧BE)は today 起点。 */
  acceptanceFromDate?: string;
  acceptanceUntilDate?: string;
  phoneRequired?: boolean;
  selfCancelDeadlineHours?: number;
  /** 店舗のデフォルト滞在時間（分）。コース未選択時の「ご滞在目安」表示に使う（旧BEは未返却のため optional。フィードバックB4） */
  defaultStayMinutes?: number;
  /**
   * 公開フォームで選べる最大人数（統合契約4）。
   * SEAT_TYPE 時は max(capacityMax×maxCombinedUnits) と受付設定 maxPartySize の小さい方を BE が返す。
   * 旧BEは未返却のため optional（未指定時は FE 既定の上限を使う）。
   */
  maxPartySize?: number;
  /**
   * 席の連結を含む実効的な最大受入人数（統合契約4・A1）。
   * 有効な席タイプ全体の max(capacityMax×maxCombinedUnits)。席タイプが無い/AGGREGATE時は null。
   * 人数変更・入力の上限警告に maxPartySize より優先して使う（旧BEは未返却のため optional）。
   */
  maxSeatCapacity?: number | null;
  /** 公開フォームで選べる最小人数（統合契約・additive。旧BEは未返却のため optional） */
  minPartySize?: number;
  // ---- フィードバック4追加（migration 022。旧BEは未返却のため optional） ----
  /** true=コース選択必須（登録コースが0件のときは強制しない）。false/未指定=席のみ予約OK */
  courseSelectionRequired?: boolean;
  /** 消費税率（%）。menus[].priceIncludingTax が無い場合の税込算出に使う（既定10） */
  taxRatePercent?: number;
  // ---- フィードバック2追加（店舗写真。旧BEは未返却のため optional） ----
  heroImageUrl?: string | null;
  galleryImageUrls?: string[];
  // ---- フィードバック3追加（個人情報の取り扱い・受付停止理由。旧BEは未返却のため optional） ----
  /** null=店舗が未設定（このとき acceptingReservations=false になる） */
  privacyPolicy?: PublicPrivacyPolicyRef | null;
  /** acceptingReservations=false のときの理由（additive なため未知値も許容する） */
  notAcceptingReason?: NotAcceptingReason | string | null;
  // ---- Wave A 追加（BE 実装中の契約・旧BEは未返却のため optional） ----
  /**
   * 承認モード（Wave A・5）。MANUAL=店舗承認後に確定 / AUTO=このまま確定。
   * 承認要否メッセージの出し分けに使う（未指定時はメッセージを出さない）。
   */
  approvalMode?: ApprovalMode;
  /**
   * オーバーブッキング許可（Wave A・migration 024）。承認制(MANUAL)店舗で
   * 卓数超過の公開予約を受け付けるか。true かつ MANUAL のとき FULL 枠も bookable=true になりうる。
   * FE では現状メッセージ・型整合のためだけに保持する（表示ロジックは BE の bookable に従う）。
   */
  allowOverbooking?: boolean;
}

/** GET /public/stores/{id}/reservations/{no}?token=（本人照会・Phase 1 追加項目含む） */
export interface ManagedReservationView {
  id: string;
  reservationNumber: string;
  userName: string;
  userEmail: string | null;
  userPhone: string | null;
  reservationTime: string;
  numberOfPeople: number;
  status: ReservationStatus;
  cancellationReason: CancellationReason | null;
  selectedMenuName: string | null;
  selectedMenuPrice: number | null;
  /** コースの税込金額（selectedMenuPrice[税抜]×taxRatePercent から BE が算出。旧BEは未返却のため optional。フィードバックC） */
  selectedMenuPriceIncludingTax?: number | null;
  responses: InquiryResponseView[];
  payment: ReservationPaymentView | null;
  // ---- Phase 1 追加 ----
  cancellable?: boolean;
  cancelDeadline?: string | null;
  // ---- フィードバック5・B7 追加（照会/確認画面に店舗の連絡手段を表示。旧BEは未返却のため optional） ----
  /** 店舗名（店舗マスタ） */
  storeName?: string | null;
  /** 住所（店舗マスタ・Google マップリンクに使う） */
  address?: string | null;
  /** 店舗への連絡先（StoreInfo.contactInfo） */
  contactInfo?: string | null;
}

/**
 * 匿名化済み予約の管理画面表示文言（Wave B）。
 * ご予約の来店日から15日経過後に個人情報（氏名/メール/電話）を非可逆匿名化するため、
 * 実データの代わりにこの安心文言を表示する。
 */
export const ANONYMIZED_INFO_LABEL = 'お客様の情報は匿名化されており復元できません';

// ---------- 表示ヘルパー ----------

/** ステータス→日本語ラベル（管理画面用） */
export const RESERVATION_STATUS_LABEL: Record<ReservationStatus, string> = {
  REQUESTED: '承認待ち',
  CONFIRMED: '確定',
  REJECTED: '却下',
  CANCELLED: 'キャンセル',
  COMPLETED: '来店済み',
  NO_SHOW: '不来店',
  PENDING_PAYMENT: '決済待ち',
  EXPIRED: '期限切れ',
};

/** ステータス→MUI Chip カラー（管理画面用） */
export const RESERVATION_STATUS_COLOR: Record<
  ReservationStatus,
  'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'
> = {
  REQUESTED: 'warning',
  CONFIRMED: 'success',
  REJECTED: 'default',
  CANCELLED: 'default',
  COMPLETED: 'info',
  NO_SHOW: 'error',
  PENDING_PAYMENT: 'warning',
  EXPIRED: 'default',
};

/** 遷移表: 現在ステータス→可能なアクション（REQUESTED→CONFIRM/REJECT/CANCEL、CONFIRMED→COMPLETE/CANCEL/NO_SHOW） */
export const AVAILABLE_STATUS_ACTIONS: Record<ReservationStatus, ReservationStatusAction[]> = {
  REQUESTED: ['CONFIRM', 'REJECT', 'CANCEL'],
  CONFIRMED: ['COMPLETE', 'CANCEL', 'NO_SHOW'],
  REJECTED: [],
  CANCELLED: [],
  COMPLETED: [],
  NO_SHOW: [],
  // 決済待ち/失効は公開フロー内部状態。スタッフの手動アクション対象にしない。
  PENDING_PAYMENT: [],
  EXPIRED: [],
};

export const STATUS_ACTION_LABEL: Record<ReservationStatusAction, string> = {
  CONFIRM: '承認する',
  REJECT: '却下する',
  CANCEL: 'キャンセルする',
  COMPLETE: '来店済みにする',
  NO_SHOW: '不来店にする',
};

/** 'YYYY-MM-DDTHH:mm:ss' → 'M/D(曜) HH:mm' 表示（JST 前提のローカル日時文字列を素直に整形） */
export const formatReservationDateTime = (isoLocal: string): string => {
  const d = new Date(isoLocal);
  if (Number.isNaN(d.getTime())) return isoLocal;
  const youbi = ['日', '月', '火', '水', '木', '金', '土'][d.getDay()];
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()}(${youbi}) ${hh}:${mm}`;
};

/** Date → 'YYYY-MM-DD'（ローカルタイム） */
export const toDateString = (d: Date): string => {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${day}`;
};
